import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Box, Typography, Stack, Grid, Button, Paper, Tabs, Tab, Divider } from '@mui/material';
import '../../styles/public-pages/platform.css';

const Platform: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const images = [
    'https://images.pexels.com/photos/3184611/pexels-photo-3184611.jpeg?auto=compress&cs=tinysrgb&w=1200&dpr=2',
    'https://images.pexels.com/photos/34642151/pexels-photo-34642151.jpeg?auto=compress&cs=tinysrgb&w=1200&dpr=2',
    'https://images.pexels.com/photos/3184357/pexels-photo-3184357.jpeg?auto=compress&cs=tinysrgb&w=1200&dpr=2',
    'https://images.pexels.com/photos/3783839/pexels-photo-3783839.jpeg?auto=compress&cs=tinysrgb&w=1200&dpr=2',
    'https://images.pexels.com/photos/6347901/pexels-photo-6347901.jpeg?auto=compress&cs=tinysrgb&w=1200&dpr=2',
  ];
  return (
    <Box className="platform-page" sx={{ bgcolor: 'background.default' }}>
      {/* Hero */}
      <Box className="platform-hero" sx={{
        pt: { xs: 12, md: 16 },
        pb: { xs: 12, md: 14 },
        background: 'radial-gradient(1200px 600px at 50% 15%, rgba(0,67,254,0.18) 0%, rgba(3,163,255,0.08) 45%, rgba(255,255,255,1) 75%)',
      }}>
        <Container className="slider-container" maxWidth="lg">
          <Box className="platform-hero__content" sx={{ textAlign: 'center', maxWidth: 920, mx: 'auto' }}>
            <Typography
              className="platform-hero__title"
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 800,
                letterSpacing: '-0.02em',
                fontSize: { xs: 36, sm: 44, md: 56 },
                lineHeight: 1.1,
              }}
            >
              Transform Your Culture,
              <br />Supercharge Your Business
            </Typography>
            <Typography className="platform-hero__subtitle" variant="h6" color="text.secondary" sx={{ mt: 2, maxWidth: 820, mx: 'auto' }}>
              Great companies aren’t built on strategy alone—they’re built on people who feel valued, heard, and empowered. SuperGauge helps you unlock the kinds of insights where your team doesn’t just show up, but actively contributes to your success.
            </Typography>
            <Stack className="platform-hero__ctas" direction={{ xs: 'column', sm: 'row' }} justifyContent="center" spacing={2} sx={{ mt: 4 }}>
              <Button
                className="platform-hero__cta platform-hero__cta--secondary"
                variant="outlined"
                color="primary"
                onClick={() => { navigate('/login'); }}
                sx={{ borderRadius: 2, px: 3, py: 1.2, textTransform: 'none' }}
              >
                Try the demo
              </Button>
              <Button
                className="platform-hero__cta platform-hero__cta--primary"
                variant="contained"
                color="primary"
                onClick={() => { navigate('/register'); }}
                sx={{ borderRadius: 2, px: 3, py: 1.2, textTransform: 'none', boxShadow: '0 6px 14px rgba(0,67,254,0.25)' }}
              >
                Get started
              </Button>
            </Stack>
          </Box>

          {/* Hero carousel */}
          <Box className="platform-hero__carousel" sx={{ mt: 6 }}>
            <Box className="platform-hero__marquee">
              <Box className="platform-hero__track">
                {images.map((src, idx) => (
                  <Paper key={`slide-a-${idx}`} className={`platform-hero__card ${idx % 3 === 0 ? 'platform-hero__card--tall' : idx % 3 === 1 ? 'platform-hero__card--short' : 'platform-hero__card--medium'}`} variant="outlined">
                    <Box
                      component="img"
                      src={src}
                      alt="platform visual"
                      className="platform-hero__img"
                    />
                  </Paper>
                ))}
              </Box>
              {/* duplicate for seamless loop */}
              <Box className="platform-hero__track" aria-hidden>
                {images.map((src, idx) => (
                  <Paper key={`slide-b-${idx}`} className={`platform-hero__card ${idx % 3 === 0 ? 'platform-hero__card--tall' : idx % 3 === 1 ? 'platform-hero__card--short' : 'platform-hero__card--medium'}`} variant="outlined">
                    <Box
                      component="img"
                      src={src}
                      alt="platform visual"
                      className="platform-hero__img"
                    />
                  </Paper>
                ))}
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Support section with stats */}
      <Container className="platform-support" maxWidth="lg" sx={{ py: { xs: 8, md: 10 } }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>
              Give your team the support and leadership they deserve
            </Typography>
            <Typography variant="body1" color="text.secondary">
              People thrive when managers coach more, not micro-manage. They know what matters most, and they’re motivated to share. SuperGauge helps leaders create the environment to capture and act on that knowledge, so it skyrockets loyalty.
            </Typography>
          </Grid>
          {/* Stats on a new full-width row */}
          <Grid item xs={12} md={12}>
            <Grid className="platform-stats" container spacing={3}>
              <Grid item xs={12} sm={12} md={4}>
                <Paper className="platform-stat" variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>10%</Typography>
                  <Typography variant="body2" color="text.secondary">Employees in the UK who actually feel excited about work</Typography>
                  <Box component="img" className="platform-stat__img" src={`${process.env.PUBLIC_URL}/stat1.svg`} alt="statistic 1" sx={{ mt: 2, width: '100%', maxWidth: 360 }} />
                </Paper>
              </Grid>
              <Grid item xs={12} sm={12} md={4}>
                <Paper className="platform-stat" variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>2.5x</Typography>
                  <Typography variant="body2" color="text.secondary">Engaged employees are 2.5 times more likely to exceed performance expectations</Typography>
                  <Box component="img" className="platform-stat__img" src={`${process.env.PUBLIC_URL}/stat2.svg`} alt="statistic 2" sx={{ mt: 2, width: '100%', maxWidth: 360 }} />
                </Paper>
              </Grid>
              <Grid item xs={12} sm={12} md={4}>
                <Paper className="platform-stat" variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>21%</Typography>
                  <Typography variant="body2" color="text.secondary">Highly engaged businesses are 21% more profitable</Typography>
                  <Box component="img" className="platform-stat__img" src={`${process.env.PUBLIC_URL}/stat3.svg`} alt="statistic 3" sx={{ mt: 2, width: '100%', maxWidth: 360 }} />
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>

      {/* Tabbed section */}
      <Container className="platform-tabs" maxWidth="lg" sx={{ pb: { xs: 10, md: 12 } }}>
        <Paper className="platform-tabs__card" variant="outlined" sx={{ borderRadius: 2 }}>
          <Box className="platform-tabs__inner" sx={{ p: { xs: 3, md: 4 } }}>
            <Typography className="platform-tabs__title" variant="h4" sx={{ fontWeight: 800, mb: 2 }}>Simple, easy, effective.</Typography>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{ borderBottom: '0px solid', borderColor: 'divider' }}
              variant="fullWidth"
              TabIndicatorProps={{ style: { display: 'none' } }}
            >
              <Tab className="platform-tab" label="Ask the right questions" />
              <Tab className="platform-tab" label="Understand the responses" />
              <Tab className="platform-tab" label="Make positive change" />
            </Tabs>

            <Grid className="platform-tabs__content" container spacing={3} sx={{ pt: 3 }}>
              <Grid
                item
                xs={12}
                md={6}
                className="platform-tabs__col platform-tabs__col--device"
                sx={{
                  backgroundImage:
                    tab === 0
                      ? `url(${process.env.PUBLIC_URL}/tab1.webp)`
                      : tab === 1
                      ? `url(${process.env.PUBLIC_URL}/tab2.webp)`
                      : tab === 2
                      ? `url(${process.env.PUBLIC_URL}/tab3.webp)`
                      : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  minHeight: 460,
                }}
              />
              <Grid item xs={12} md={6} className="platform-tabs__col platform-tabs__col--content">
                <Box className="platform-tabpane" sx={{ p: { xs: 1, md: 2 } }}>
                  {tab === 0 && (
                    <>
                      <Typography className="platform-tabpane__title" variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        You don’t need to be a qualified HR practitioner to ask the right questions.
                        </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        Get access to a library of surveys that have been designed to capture useful and honest feedback and insights.
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        Simply choose the survey you want to use and pick a team to send it to.
                      </Typography>
                      <Button className="platform-tabpane__cta" variant="contained" onClick={() => { navigate('/register'); }}>Get started</Button>
                    </>
                  )}
                  {tab === 1 && (
                    <>
                      <Typography className="platform-tabpane__title" variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Take the stress and time out of collected and compiling responses.</Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        Get live updates and responses straight to your dashboard.
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        See an overall engagement score as well as detailed responses to each question and metric.
                      </Typography>
                      <Button className="platform-tabpane__cta" variant="contained" onClick={() => { navigate('/register'); }}>See a demo</Button>
                    </>
                  )}
                  {tab === 2 && (
                    <>
                      <Typography className="platform-tabpane__title" variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Whether your a small team or a big business - happy people means success.</Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        SuperGauge lets you know exactly where the room for improvement exists and how to achieve make that positive change.
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        Our network of qualified experts are also available if you need a little extra support.
                      </Typography>
                      <Button className="platform-tabpane__cta" variant="contained" onClick={() => { navigate('/register'); }}>Make a change</Button>
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>

      {/* Site footer (shared with landing page) */}
      <Box className="site-footer" sx={{ pt: 10, pb: 4, bgcolor: '#000430', color: '#ffffff' }}>
        {/* Middle nav columns */}
        <Container className="site-footer__links" maxWidth="lg" sx={{ mt: 8 }}>
          <Grid className="site-footer__grid" container spacing={6}>
            <Grid className="site-footer__brand" item xs={12} md={4}>
              <Box
                component="img"
                className="site-footer__logo"
                src={`${process.env.PUBLIC_URL}/sglogo.svg`}
                alt="SuperGauge"
                sx={{ height: 32, width: 'auto', display: 'block', mb: 1.5 }}
              />
              <Typography className="site-footer__branddesc" variant="body2" sx={{ mt: 1.5, color: 'rgba(255,255,255,0.8)' }}>
                Gather the insights you need to make people-driven, data-informed decisions about your company culture.
              </Typography>
            </Grid>
            <Grid className="site-footer__col" item xs={12} md={2}>
              {/* Reserved for future footer nav links */}
            </Grid>
            <Grid className="site-footer__col" item xs={12} md={2}>
              {/* Reserved for future footer nav links */}
            </Grid>
            <Grid className="site-footer__col" item xs={12} md={2}>
              {/* Reserved for future footer nav links */}
            </Grid>
          </Grid>
        </Container>

        {/* Bottom bar */}
        <Container className="site-footer__bar" maxWidth="lg" sx={{ mt: 6 }}>
          <Box className="site-footer__bar-inner" sx={{ borderTop: '1px solid', borderColor: 'rgba(255,255,255,0.2)', pt: 2 }}>
            <Grid className="site-footer__bar-grid" container alignItems="center">
              <Grid className="site-footer__bar-left" item xs={12} md={6}>
                <Typography className="site-footer__copyright" variant="caption">© {new Date().getFullYear()} SuperGauge</Typography>
              </Grid>
              <Grid className="site-footer__bar-right" item xs={12} md={6}>
                <Box className="site-footer__contact" sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                  <Typography className="site-footer__link" variant="caption">Contact</Typography>
                  <Typography className="site-footer__link" variant="caption">Terms</Typography>
                  <Typography className="site-footer__link" variant="caption">Privacy policy</Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Platform;
